import type { ReactNode } from "react";

import ActividadesDashboard from "./sections/ActividadesDashboard";
import ActividadesListSection from "./sections/ActividadesListSection";
import BacklogSection from "./sections/BacklogSection";
import RoadmapSection from "./sections/RoadmapSection";
import CatalogoSection from "./sections/CatalogoSection";

// ---------------------------------------------------------------------------
// Single source of truth for the "Gestión de Staff" navigation tree (issue #60).
// Drives:
//   - the apps-sidebar category/apps (appsConfig.tsx)
//   - the per-functionality sub-menu (StaffLayout)
//   - the nested routes (router/config.tsx via routes.tsx)
// Mirrors the Nuvex Access scaffold so both surfaces stay consistent.
//
// Épica #1299: Actividades funciona como un Jira — Dashboard de insights,
// Tablero Kanban del sprint activo, Backlog y sprints, Roadmap y Catálogo por
// puesto. Los ids de menú son también las llaves de derechos
// `menu:gestion-staff-actividades.<id>` (packages/entitlements).
// ---------------------------------------------------------------------------

export interface NavItem {
  id: string; // route segment under the functionality
  label: string;
  icon: string; // remixicon class for the sub-menu
  element: ReactNode;
}

export interface Functionality {
  slug: string; // route base: /gestion-staff/<slug>
  name: string;
  description: string;
  iconClass: string; // apps-sidebar icon (remixicon)
  color: string; // tailwind bg color for the app tile
  remixIcon: string; // sub-menu / header icon
  menu: NavItem[];
}

export const FUNCTIONALITIES: Functionality[] = [
  {
    slug: "actividades",
    name: "Actividades",
    description:
      "Centro de trabajo del equipo: backlog, sprints, tablero Kanban, roadmap y catálogo de actividades por puesto",
    iconClass: "ri-clipboard-line",
    color: "bg-violet-500",
    remixIcon: "ri-task-line",
    menu: [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: "ri-dashboard-line",
        element: <ActividadesDashboard />,
      },
      {
        id: "actividades",
        label: "Tablero",
        icon: "ri-kanban-view",
        element: <ActividadesListSection />,
      },
      {
        id: "backlog",
        label: "Backlog y sprints",
        icon: "ri-list-check-3",
        element: <BacklogSection />,
      },
      {
        id: "roadmap",
        label: "Roadmap",
        icon: "ri-road-map-line",
        element: <RoadmapSection />,
      },
      {
        id: "catalogo",
        label: "Catálogo",
        icon: "ri-clipboard-line",
        element: <CatalogoSection />,
      },
      // «Integraciones» (CC-CONSOLE #1132) no se monta aquí: usa el panel
      // centralizado de CC-CONSOLE; en esta plataforma las llaves son por compañía.
    ],
  },
];

export const STAFF_BASE = "/gestion-staff";

/** Default landing route for the whole section (first functionality dashboard). */
export const staffDefaultRoute = `${STAFF_BASE}/${FUNCTIONALITIES[0].slug}/${FUNCTIONALITIES[0].menu[0].id}`;

/** Route to a functionality's first menu item (used by the apps-sidebar tiles). */
export const functionalityRoute = (f: Functionality) =>
  `${STAFF_BASE}/${f.slug}/${f.menu[0].id}`;

/** Ruta del tablero Kanban; con `ticketId` abre el detalle de ese ticket (lo usa Horas Trabajadas). */
export const kanbanRoute = (ticketId?: string | null) =>
  `${STAFF_BASE}/actividades/actividades${ticketId ? `?ticket=${encodeURIComponent(ticketId)}` : ""}`;

export const backlogRoute = `${STAFF_BASE}/actividades/backlog`;
